import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, Facebook, Instagram, Youtube, Linkedin, User, LogOut, LayoutDashboard, ChevronDown, Shield } from 'lucide-react';
import { company, socials, heroImages } from '../mock/data';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';

const iconMap = { facebook: Facebook, instagram: Instagram, youtube: Youtube, linkedin: Linkedin };
const TikTokIcon = (props) => (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.77 20.1a6.34 6.34 0 0 0 10.9-4.42V8.71a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.85-.14Z"/></svg>);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings();
  const phone = settings?.company_phone || company.phone;
  const phoneRaw = (settings?.company_phone || company.phone).replace(/[^0-9+]/g, '');
  const email = settings?.company_email || company.email;
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setUserMenu(false); }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onLogout = () => { logout(); setUserMenu(false); navigate('/'); };

  const nav = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/send', label: 'Send Parcel', highlight: true },
    { to: '/track', label: 'Track' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-black text-white text-[13px] hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <a href={`tel:${phoneRaw}`} className="flex items-center gap-2 hover:text-[#c8102e] transition-colors">
              <Phone className="w-3.5 h-3.5" /> <span className="font-medium tracking-wide">{phone}</span>
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-[#c8102e] transition-colors">
              <Mail className="w-3.5 h-3.5" /> <span className="font-medium tracking-wide">{email}</span>
            </a>
          </div>
          <div className="flex items-center gap-1">
            {socials.map((s) => {
              const Icon = s.icon === 'tiktok' ? TikTokIcon : iconMap[s.icon];
              if (!Icon) return null;
              return (
                <a key={s.name} href={s.url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full hover:bg-[#c8102e] flex items-center justify-center transition-colors" aria-label={s.name}>
                  <Icon className="w-3.5 h-3.5" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className={`bg-white transition-all ${scrolled ? 'shadow-md' : 'border-b border-neutral-100'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={heroImages.logo} alt="Lanka Express Logistics" className="h-20 md:h-24 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} className={`relative font-medium text-[14px] tracking-wide transition-colors py-2 ${location.pathname === n.to ? 'text-[#c8102e]' : n.highlight ? 'text-[#c8102e] hover:text-black' : 'text-black hover:text-[#c8102e]'}`}>
                {n.label}
                {location.pathname === n.to && <span className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-[#c8102e]" />}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="relative" ref={menuRef}>
                <button onClick={() => setUserMenu(!userMenu)} className="hidden sm:inline-flex items-center gap-2 pl-2 pr-3 py-1.5 border border-neutral-200 hover:border-black transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#c8102e] flex items-center justify-center text-white font-serif-display font-bold text-sm">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-semibold text-black max-w-[100px] truncate">{user.name.split(' ')[0]}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-neutral-500 transition-transform ${userMenu ? 'rotate-180' : ''}`} />
                </button>
                {userMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white shadow-2xl border border-neutral-200 overflow-hidden z-50">
                    <div className="p-4 border-b border-neutral-100">
                      <div className="font-serif-display font-bold text-black">{user.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{user.email}</div>
                    </div>
                    <Link to="/account" className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50">
                      <LayoutDashboard className="w-4 h-4 text-[#c8102e]" /> My Account
                    </Link>
                    {(user.is_admin || user.is_employee) && (
                      <Link to="/employee" className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50 border-t border-neutral-100">
                        <LayoutDashboard className="w-4 h-4 text-[#c8102e]" /> Staff Dashboard
                      </Link>
                    )}
                    {user.is_admin && (
                      <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50 border-t border-neutral-100 bg-neutral-50/50">
                        <Shield className="w-4 h-4 text-[#c8102e]" /> <span className="font-semibold">Admin Panel</span>
                      </Link>
                    )}
                    <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50 border-t border-neutral-100">
                      <LogOut className="w-4 h-4 text-[#c8102e]" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold tracking-wide text-black hover:text-[#c8102e] transition-colors">
                  <User className="w-4 h-4" /> Sign In
                </Link>
                <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c8102e] text-white text-[13px] font-semibold tracking-wide rounded-md hover:bg-black transition-colors">
                  Sign Up
                </Link>
              </div>
            )}
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="w-6 h-6 text-black" /> : <Menu className="w-6 h-6 text-black" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t bg-white">
            <div className="px-6 py-4 flex flex-col gap-4">
              {nav.map((n) => (
                <Link key={n.to} to={n.to} className={`font-semibold ${location.pathname === n.to ? 'text-[#c8102e]' : 'text-black'}`}>{n.label}</Link>
              ))}
              {user ? (
                <>
                  <Link to="/account" className="font-semibold text-black">My Account</Link>
                  {user.is_admin && <Link to="/admin" className="font-semibold text-[#c8102e]">Admin Panel</Link>}
                  <button onClick={onLogout} className="text-left font-semibold text-[#c8102e]">Sign Out</button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-2 border-t border-neutral-100">
                  <Link to="/login" className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-black text-black font-semibold rounded-md">Sign In</Link>
                  <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#c8102e] text-white font-semibold rounded-md">Sign Up</Link>
                </div>
              )}
              <a href={`tel:${phoneRaw}`} className="inline-flex items-center justify-center gap-2 px-5 py-3 text-black font-semibold border-t border-neutral-100 pt-3">
                <Phone className="w-4 h-4" /> {phone}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
